
import React, { useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import { Outlet } from 'react-router-dom';
import { CasinoHeader } from "@/components/CasinoHeader";
import { CasinoSidebarNew } from "@/components/CasinoSidebarNew";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { Footer } from "@/components/Footer";
import { ChatPanel } from "@/components/chat/ChatPanel";
import { SupportChatButton } from "@/components/support-chat/SupportChatButton";
import { SupportChatVisibilityProvider } from "@/contexts/SupportChatVisibilityContext";
import { useChat } from "@/contexts/ChatContext";
import { useAuth } from "@/contexts/AuthContext";
import { storage } from "@/lib/storage";

// Helper to check auth state
const useIsAuthenticated = () => {
  const { session } = useAuth();
  return !!session;
};

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';
const CHAT_PANEL_WIDTH = 'w-80'; // 320px

/** Below this width, sidebar (expanded) and chat panel are mutually exclusive */
const EXCLUSIVE_BREAKPOINT = 1130;

function subscribeToResize(cb: () => void) {
  window.addEventListener('resize', cb);
  return () => window.removeEventListener('resize', cb);
}
function getWidth() { return window.innerWidth; }
function useWindowWidth() { return useSyncExternalStore(subscribeToResize, getWidth, () => 1400); }

interface ResponsiveLayoutProps {
  children?: React.ReactNode;
  className?: string;
}

export const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({ 
  children, 
  className = "" 
}) => {
  const isAuthenticated = useIsAuthenticated();
  const { isOpen: isChatOpen, closeChat } = useChat();
  const windowWidth = useWindowWidth();

  // Sidebar collapse state with storage persistence
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = storage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === 'true';
  });

  // Persist collapse state to storage
  useEffect(() => {
    storage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
  }, [isCollapsed]);

  const isNarrow = windowWidth < EXCLUSIVE_BREAKPOINT;

  // Enforce mutual exclusion at narrow widths:
  // - When chat opens → collapse sidebar
  // - When viewport shrinks while both are open → collapse sidebar (chat takes priority)
  useEffect(() => {
    if (isNarrow && !isCollapsed && isChatOpen) {
      setIsCollapsed(true);
    }
  }, [isNarrow, isChatOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sidebar toggle — if expanding sidebar at narrow width, close chat
  const toggleSidebar = useCallback(() => {
    setIsCollapsed(prev => {
      const willExpand = prev; // prev === true means currently collapsed → expanding
      if (willExpand && isNarrow && isChatOpen) {
        closeChat();
      }
      return !prev;
    });
  }, [isNarrow, isChatOpen, closeChat]);

  // Use Outlet for router-level layout, or children for direct wrapping
  const content = children ?? <Outlet />;

  const layoutContent = (
    <div className="min-h-screen bg-background text-foreground max-w-screen overflow-x-hidden">
      {/* Desktop Layout */}
      <div className="hidden lg:flex h-screen max-w-full">
        <div 
          className={`flex-shrink-0 h-full transition-all duration-300 ease-in-out overflow-visible relative z-50 ${
            isCollapsed ? 'w-20' : 'w-64'
          }`}
        >
          <CasinoSidebarNew isCollapsed={isCollapsed} onToggleCollapse={toggleSidebar} />
        </div>
        <div className="flex-1 flex flex-col h-full min-w-0 max-w-full overflow-x-hidden bg-background">
          <div className="flex-shrink-0 w-full">
            <CasinoHeader isSidebarCollapsed={isCollapsed} />
          </div>
          <main id="main-scroll-container" className={`flex-1 px-2 sm:px-4 lg:px-6 pb-6 pt-2 overflow-y-auto max-w-full min-w-0 ${className}`}>
            {content}
            <Footer />
          </main>
        </div>

        {/* Desktop Chat Panel — right side, collapsible */}
        {isAuthenticated && <DesktopChatPanel />}
      </div>

      {/* Mobile Layout */}
      {/* Header: balance row ~56px + jackpot ~24px + 1px separator = ~81px + safe-area-inset-top */}
      <div className="lg:hidden min-h-screen max-w-full overflow-x-hidden bg-background">
        <CasinoHeader />
        <main className={`px-4 pt-[calc(88px+env(safe-area-inset-top))] pb-[calc(5rem+env(safe-area-inset-bottom))] min-h-screen max-w-full ${className}`}>
          {content}
          <Footer />
        </main>
        <MobileBottomNav />

        {/* Mobile Chat Drawer */}
        {isAuthenticated && <MobileChatDrawer />}
      </div>

      {/* Global Support Chat Button - shows floating bubble when there's an active support conversation */}
      {isAuthenticated && <SupportChatButton />}
    </div>
  );
  
  // Wrap with SupportChatVisibilityProvider when authenticated to enable global chat control
  if (isAuthenticated) {
    return (
      <SupportChatVisibilityProvider>
        {layoutContent}
      </SupportChatVisibilityProvider>
    );
  }
  
  return layoutContent;
};

/**
 * Desktop chat panel — slides in from the right.
 */
const DesktopChatPanel: React.FC = () => {
  const { isOpen, closeChat } = useChat();

  return (
    <div
      className={`flex-shrink-0 h-full transition-all duration-300 ease-in-out overflow-hidden ${
        isOpen ? CHAT_PANEL_WIDTH : 'w-0'
      }`}
    >
      {isOpen && (
        <div className={`h-full ${CHAT_PANEL_WIDTH}`}>
          <ChatPanel onClose={closeChat} />
        </div>
      )}
    </div>
  );
};

/**
 * Mobile chat drawer — slides up from the bottom, sits behind the bottom nav / quick actions / menu.
 * z-[230] keeps it below bottom nav (z-250), quick actions (z-240-245), and menu sheet (z-241).
 * The panel extends flush to the top of the nav bar (64px). The center logo overhang (28px)
 * is handled via bottom padding inside ChatPanel so the input clears it without leaving a gap.
 */
const MobileChatDrawer: React.FC = () => {
  const { isMobileOpen, closeMobileChat } = useChat();

  if (!isMobileOpen) return null;

  return (
    <>
      {/* Backdrop — sits behind bottom nav so nav is still tappable */}
      <div
        className="fixed inset-0 z-[230] bg-black/60 transition-opacity duration-300"
        style={{ bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))' }}
        onClick={closeMobileChat}
      />
      {/* Chat panel — flush to nav bar edge, internal padding handles logo overhang */}
      <div
        className="fixed left-0 right-0 top-0 z-[231] bg-background flex flex-col animate-in slide-in-from-bottom duration-300"
        style={{
          height: 'calc(100% - 64px - env(safe-area-inset-bottom, 0px))',
          paddingTop: 'env(safe-area-inset-top, 0px)',
        }}
      >
        <ChatPanel onClose={closeMobileChat} className="flex-1" />
      </div>
    </>
  );
};
